// Оформление узла и личные настройки (ADR-027, ADR-028). Значения — ключи токенов Дизайна; своих
// цветов и шрифтов клиент не вводит, он только выбирает из объявленного набора.

import type { NodeInfo } from "@yofis/proto/yofis/v1/node_pb";

const accents: Record<number, string> = {
  1: "teal", 2: "indigo", 3: "amber", 4: "raspberry", 5: "forest", 6: "graphite",
};

/**
 * Шрифт: два уровня, как у акцента (ADR-028). Узел задаёт значение по умолчанию и решает, можно ли
 * сотруднику выбирать своё; личный выбор живёт на устройстве и поверх узла. Набор фиксирован — набор
 * токенов Дизайна, произвольные шрифты не подключаются.
 */
export const fonts = ["onest", "golos", "manrope", "inter", "plex"] as const;
export type Font = (typeof fonts)[number];

/** Номера `yofis.v1.Font` → ключи токенов. */
const nodeFonts: Record<number, Font> = { 1: "onest", 2: "golos", 3: "manrope", 4: "inter", 5: "plex" };

/** Тема: выбор пользователя или системная. */
export type Theme = "system" | "light" | "dark";

/**
 * Применяет оформление узла: акцент и шрифт объявляет узел, тему выбирает человек. Личный выбор шрифта
 * накладывается поверх — но только если узел его разрешил.
 */
export function applyBranding(node: NodeInfo | undefined): void {
  const root = document.documentElement;
  const accent = accents[node?.branding?.accent ?? 0];
  if (accent !== undefined) {
    root.dataset["accent"] = accent;
  }
  const nodeFont = nodeFonts[node?.branding?.font ?? 0] ?? "onest";
  const personal = node?.branding?.allowUserFont === false ? undefined : savedFont();
  root.dataset["font"] = personal ?? nodeFont;
}

/** Разрешает ли узел личный выбор шрифта; по умолчанию — да. */
export function userFontAllowed(node: NodeInfo | undefined): boolean {
  return node?.branding?.allowUserFont !== false;
}

/** Шрифт узла по умолчанию: его показываем как выбор «как у компании». */
export function nodeFont(node: NodeInfo | undefined): Font {
  return nodeFonts[node?.branding?.font ?? 0] ?? "onest";
}

/** Шрифт интерфейса: личная настройка, хранится локально. */
export function applyFont(font: Font): void {
  document.documentElement.dataset["font"] = font;
  try {
    localStorage.setItem("yofis.font", font);
  } catch {
    // см. applyTheme
  }
}

export function savedFont(): Font | undefined {
  try {
    const saved = localStorage.getItem("yofis.font");
    if (saved !== null && (fonts as readonly string[]).includes(saved)) {
      return saved as Font;
    }
  } catch {
    // см. applyTheme
  }
  return undefined;
}

/** Сбрасывает личный выбор: интерфейс возвращается к шрифту компании. */
export function clearFont(): void {
  try {
    localStorage.removeItem("yofis.font");
  } catch {
    // см. applyTheme
  }
}

/** Тема хранится локально: это выбор человека на этом устройстве, а не настройка узла. */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset["theme"] = theme;
  try {
    localStorage.setItem("yofis.theme", theme);
  } catch {
    // Приватный режим запрещает localStorage — тема просто не переживёт перезагрузку.
  }
}

export function savedTheme(): Theme {
  try {
    const saved = localStorage.getItem("yofis.theme");
    if (saved === "light" || saved === "dark" || saved === "system") {
      return saved;
    }
  } catch {
    // см. applyTheme
  }
  return "system";
}
